"use client";

import { useState, useEffect } from "react";
import { Cookie, X, Check, Settings, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";

const CONSENT_KEY = "hill_medical_consent_v2";

type ConsentState = {
  ad_storage: "granted" | "denied";
  ad_user_data: "granted" | "denied";
  ad_personalization: "granted" | "denied";
  analytics_storage: "granted" | "denied";
  personalization_storage: "granted" | "denied";
  functionality_storage: "granted";
  security_storage: "granted";
};

const DEFAULT_DENIED: ConsentState = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  personalization_storage: "denied",
  functionality_storage: "granted",
  security_storage: "granted",
};

const DEFAULT_GRANTED: ConsentState = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
  personalization_storage: "granted",
  functionality_storage: "granted",
  security_storage: "granted",
};

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<ConsentState>(DEFAULT_DENIED);

  useEffect(() => {
    const savedConsent = localStorage.getItem(CONSENT_KEY);
    if (!savedConsent) {
      setTimeout(() => setShowBanner(true), 2000);
    } else {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
        applyConsent(parsed);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const applyConsent = (state: ConsentState) => {
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("consent", "update", state);
    }
    window.dispatchEvent(new CustomEvent("analytics-consent-updated", { detail: state }));
  };

  const handleAcceptAll = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(DEFAULT_GRANTED));
    setConsent(DEFAULT_GRANTED);
    applyConsent(DEFAULT_GRANTED);
    setShowBanner(false);
  };

  const handleDeclineAll = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(DEFAULT_DENIED));
    setConsent(DEFAULT_DENIED);
    applyConsent(DEFAULT_DENIED);
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    applyConsent(consent);
    setShowBanner(false);
  };

  const toggleConsent = (key: keyof ConsentState) => {
    if (key === "functionality_storage" || key === "security_storage") return;
    setConsent((prev) => ({
      ...prev,
      [key]: prev[key] === "granted" ? "denied" : "granted",
    }));
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-4 left-4 right-4 z-[9999] md:left-auto md:max-w-md lg:max-w-lg"
      >
        <div className="bg-white/80 backdrop-blur-xl border border-border shadow-2xl rounded-[24px] overflow-hidden">
          <div className="p-6">
            {!showSettings ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <Cookie className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-display font-bold text-foreground">
                    Sütik és Adatvédelem
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Weboldalunk sütiket használ a felhasználói élmény javítása, a forgalom elemzése és releváns hirdetések megjelenítése érdekében. Kérjük, válassza ki az Önnek megfelelő beállításokat.
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 min-w-[140px] bg-primary text-primary-foreground font-bold"
                  >
                    Mindent Elfogadok
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowSettings(true)}
                    className="flex-1 min-w-[140px] font-bold border-border"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Beállítások
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDeclineAll}
                    className="w-full text-xs font-medium text-muted-foreground hover:text-foreground"
                  >
                    Csak a szükségesek
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-6 h-6 text-primary" />
                    <h3 className="text-lg font-display font-bold text-foreground">
                      Részletes Beállítások
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-2 hover:bg-secondary rounded-full transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-3 py-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                  {/* Essential */}
                  <div className="flex items-center justify-between p-3 rounded-xl bg-secondary/30 border border-border/50">
                    <div>
                      <p className="text-sm font-bold text-foreground">Szükséges sütik</p>
                      <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest mt-0.5">Mindig aktív</p>
                    </div>
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                      <Check className="w-4 h-4" />
                    </div>
                  </div>

                  {/* Analytics */}
                  <button
                    onClick={() => toggleConsent("analytics_storage")}
                    className={`flex items-center justify-between w-full p-3 rounded-xl border transition-all ${
                      consent.analytics_storage === "granted"
                        ? "bg-primary/5 border-primary/20"
                        : "bg-background border-border hover:bg-secondary/50"
                    }`}
                  >
                    <div className="text-left">
                      <p className="text-sm font-bold text-foreground">Statisztikai sütik</p>
                      <p className="text-xs text-muted-foreground">Látogatottsági adatok elemzése</p>
                    </div>
                    <div className={`w-10 h-5 rounded-full relative transition-colors ${
                      consent.analytics_storage === "granted" ? "bg-primary" : "bg-neutral-300"
                    }`}>
                      <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${
                        consent.analytics_storage === "granted" ? "left-6" : "left-1"
                      }`} />
                    </div>
                  </button>

                  {/* Marketing */}
                  <button
                    onClick={() => {
                        toggleConsent("ad_storage");
                        toggleConsent("ad_user_data");
                        toggleConsent("ad_personalization");
                    }}
                    className={`flex items-center justify-between w-full p-3 rounded-xl border transition-all ${
                      consent.ad_storage === "granted"
                        ? "bg-primary/5 border-primary/20"
                        : "bg-background border-border hover:bg-secondary/50"
                    }`}
                  >
                    <div className="text-left">
                      <p className="text-sm font-bold text-foreground">Marketing sütik</p>
                      <p className="text-xs text-muted-foreground">Releváns rirdetések megjelenítése</p>
                    </div>
                    <div className={`w-10 h-5 rounded-full relative transition-colors ${
                      consent.ad_storage === "granted" ? "bg-primary" : "bg-neutral-300"
                    }`}>
                      <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${
                        consent.ad_storage === "granted" ? "left-6" : "left-1"
                      }`} />
                    </div>
                  </button>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleSaveSettings}
                    className="flex-1 bg-primary text-primary-foreground font-bold shadow-lg"
                  >
                    Választott Beállítások Mentése
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
